import * as React from 'react';
import { useMountEffect } from '@react-hookz/web/esm';
import { Box, spacing } from '@apia/theme';
import { useUpdateEffect } from 'ahooks';
import { Confirm } from '../..';
import { Modal, TModal } from '@apia/components';
import useModal from '../../../../hooks/useModal';
import { IConfirm } from '../layout/Confirm';

export type TConfirmModal = Omit<TModal, 'onClose'> & {
  children?: React.ReactNode;
  confirmProps?: Omit<IConfirm, 'onConfirmOk' | 'onConfirmCancel' | 'children'>;
  /**
   * Determina si el modal debe abrirse al ser montado o no.
   */
  isDefaultOpen?: boolean;
  /**
   * El método onConfirm permite pasar un callback que devuelva una promesa. En
   * caso de ser así, si esta promesa resuelve a true, el modal será cerrado o
   * en caso contrario permanecerá abierto. Es útil en situaciones en donde es
   * necesario corroborar una condición antes de cerrar el modal. En la
   * situación en que se decida mantener el modal abierto, está a cargo del
   * usuario del componente realizar las indicaciones visuales correspondientes
   * para indicar al usuario el motivo por el cual el modal no fue cerrado.
   */
  onConfirm?: () => Promise<boolean> | boolean | void;
  /**
   * El método onCancel será llamado siempre que un usuario cierre el modal,
   * excepto cuando haya presionado en onConfirm.
   */
  onCancel?: () => void;
  /**
   * Permite introducir html directamente dentro del contenedor del modal.
   */
  html?: string;
};

/**
 * El ConfirmModal no es un diálogo de confirmación, para utilizar un diálogo
 * de confirmación, utilice el método confirm que se encuentra en
 * ConfirmProvider.
 *
 * En cambio el ConfirmModal es un modal que permite realizar cualquier tipo de
 * renderizado dentro, con un botón de confirmar y otro de cancelar. Su
 * utilidad es la de controlar la tecla escape, el click en el overlay, el
 * botón cerrar y el botón de cancelar haciendo que todos ellos resuelvan a
 * onCancel.
 *
 * 
 * Cuando se pasa un handler para onCancel, se mostrará el botón de cancelar y 
 * se llamará a este handler cada vez que:
 * - Se presione el botón cancelar.
 * - Se haga click en el overlay para cerrar el modal.
 * - Se presione escape para cerrar el modal.
 * - Se haga clic en el botón cerrar.
 * 
 * Cuando no se pasa handler para onCancel, no se mostrará el botón de cancelar
 * y cada uno de los eventos que llamabarían a onCancel, llamarán a onConfirm.
 * 
 * Nótese que el estado de abierto o cerrado del modal debe ser controlado 
 * desde afuera. Vea el ejemplo siguiente para conocer la forma más sencilla de 
 * hacerlo:
 * 
 * @example
 * 
 *import { useBoolean } from 'ahooks';

 ...

 *const [isOpen, { setTrue, setFalse }] = useBoolean(false);
  return (
    <Box sx={{ padding: 50 }}>
      <Button onClick={setTrue}>OpenModal</Button>
      <ConfirmModal
        isOpen={isOpen}
        onCancel={() => console.log('CANCEL')}
        onConfirm={() => console.log('CONFIRM')}
        onExited={setFalse}
      />
    </Box>
  );

*
 * @see ConfirmProvider
 */
const ConfirmModal = React.forwardRef<HTMLDivElement, TConfirmModal>(
  (
    {
      children = null,
      confirmProps,
      html = '',
      initialFocusGetter,
      isDefaultOpen,
      isOpen,
      onConfirm,
      onCancel,
      onExited,
      ...props
    },
    ref,
  ): JSX.Element | null => {
    const {
      show: showModal,
      onClose: hideModal,
      onExited: hookOnExited,
      ...modalProps
    } = useModal(
      React.useMemo(
        () => ({
          onExited,
          isDefaultOpen: isOpen || isDefaultOpen || false,
          debugPerformanceName: 'ConfirmModal useModal',
        }),
        [isDefaultOpen, isOpen, onExited],
      ),
    );

    useUpdateEffect(() => {
      if (isOpen) showModal();
      else hideModal();
    }, [isOpen]);

    const confirmOKHandler = React.useCallback(() => {
      void (async () => {
        if (onConfirm && (await onConfirm()) !== false) hideModal();
      })();
    }, [hideModal, onConfirm]);

    const confirmCancelHandler = React.useCallback(() => {
      if (onCancel) onCancel();
      else if (onConfirm) void onConfirm();
      hideModal();
    }, [hideModal, onCancel, onConfirm]);

    useMountEffect(() => {
      if (isOpen === undefined || isOpen === true) showModal();
    });

    return (
      <Modal
        onClose={confirmCancelHandler}
        initialFocusGetter={React.useCallback(
          (modalRef: HTMLElement) => {
            return (
              initialFocusGetter?.(modalRef) ??
              (modalRef?.querySelector(
                '.confirm__confirmButton',
              ) as HTMLElement)
            );
          },
          [initialFocusGetter],
        )}
        {...props}
        onExited={hookOnExited}
        {...modalProps}
        ref={ref}
      >
        <Confirm
          {...confirmProps}
          onConfirm={confirmOKHandler}
          onCancel={onCancel ? confirmCancelHandler : undefined}
        >
          {html && !children && (
            <Box
              sx={{ m: spacing(0), p: spacing(0) }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          )}
          {children}
        </Confirm>
      </Modal>
    );
  },
);

export default ConfirmModal;
